// src/pages/RhDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { validationsAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Timer, ReceiptText, CalendarX, Activity, Users, ClipboardCheck,
  ArrowRight, RefreshCw, CheckCircle2, Clock, XCircle, AlertCircle,
  ChevronRight, Briefcase, HeartHandshake, FolderOpen, Plus,
  TrendingUp, Zap, AlertTriangle, Calendar, FilePlus, Download,
} from 'lucide-react';

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
                   'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS_FR   = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}
function fmtDateShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')} ${MONTHS_FR[d.getMonth()].slice(0,3)}`;
}
function daysUntil(iso) {
  const diff = new Date(iso) - new Date();
  return Math.ceil(diff / 86400000);
}
function daysBetween(d1, d2) {
  return Math.ceil((new Date(d2) - new Date(d1)) / 86400000) + 1;
}

// ── Hero ──────────────────────────────────────────────────────
function Hero({ user, totalPending, onRefresh, loading }) {
  const now  = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';

  return (
    <div style={{
      background: 'linear-gradient(135deg, #064e3b 0%, #065f46 55%, #047857 100%)',
      padding: '32px 36px 28px',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Décorations géométriques */}
      <div style={{ position: 'absolute', top: -30, right: -30, width: 180, height: 180,
                    borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }}/>
      <div style={{ position: 'absolute', bottom: -50, right: 120, width: 120, height: 120,
                    borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }}/>
      <div style={{ position: 'absolute', top: 20, right: 200, width: 6, height: 6,
                    borderRadius: '50%', background: 'rgba(167,243,208,0.4)', pointerEvents: 'none' }}/>
      <div style={{ position: 'absolute', top: 50, right: 160, width: 4, height: 4,
                    borderRadius: '50%', background: 'rgba(167,243,208,0.25)', pointerEvents: 'none' }}/>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%',
                          background: 'rgba(167,243,208,0.18)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HeartHandshake size={20} color="#6ee7b7" />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6ee7b7',
                            textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Ressources Humaines
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ffffff', lineHeight: 1.1 }}>
                {greeting}, {user?.prenom || user?.nom || 'RH'} !
              </div>
            </div>
          </div>
          <div style={{ marginLeft: 50, marginTop: 4, color: 'rgba(167,243,208,0.8)', fontSize: '0.875rem' }}>
            {DAYS_FR[now.getDay()]} {now.getDate()} {MONTHS_FR[now.getMonth()]} {now.getFullYear()}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <button onClick={onRefresh} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6,
                     padding: '8px 14px', borderRadius: 9,
                     border: '1px solid rgba(167,243,208,0.3)',
                     background: 'rgba(167,243,208,0.08)',
                     color: '#6ee7b7', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
            <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}/>
            Actualiser
          </button>
          {totalPending > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 16px', borderRadius: 24,
                          background: 'rgba(251,191,36,0.15)',
                          border: '1px solid rgba(251,191,36,0.3)' }}>
              <Zap size={14} color="#fbbf24"/>
              <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: '0.875rem' }}>
                {totalPending} élément{totalPending > 1 ? 's' : ''} en attente
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── KPI Card ─────────────────────────────────────────────────
function KpiCard({ icon: Icon, color, value, label, sub, to, urgent, navigate }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={() => to && navigate(to)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'var(--bg-card)',
        border: `1.5px solid ${hov && to ? color : urgent && value > 0 ? `${color}44` : 'var(--border)'}`,
        borderRadius: 14, padding: '20px 20px 16px',
        cursor: to ? 'pointer' : 'default',
        transform: hov && to ? 'translateY(-3px)' : 'none',
        boxShadow: hov && to ? `0 12px 30px ${color}1a` : urgent && value > 0 ? `0 0 0 1px ${color}22` : 'none',
        transition: 'all 0.18s', position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                    background: urgent && value > 0 ? color : `${color}44`,
                    borderRadius: '14px 14px 0 0' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, marginTop: 4 }}>
        <div style={{ width: 42, height: 42, borderRadius: 11, background: `${color}14`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={21} color={color} />
        </div>
        {to && <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />}
      </div>
      <div style={{ fontSize: '2.5rem', fontWeight: 900, color: urgent && value > 0 ? color : 'var(--text-primary)',
                    lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color, marginTop: 6, textTransform: 'uppercase',
                    letterSpacing: '0.03em' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Pending queue ─────────────────────────────────────────────
const TABS = [
  { key: 'ts',     label: 'Feuilles',  icon: Timer,        color: '#0891B2' },
  { key: 'abs',    label: 'Congés',    icon: CalendarX,    color: '#7C3AED' },
  { key: 'nf',     label: 'Frais',     icon: ReceiptText,  color: '#D97706' },
  { key: 'arrets', label: 'Arrêts',    icon: Activity,     color: '#DC2626' },
  { key: 'docs',   label: 'Documents', icon: FolderOpen,   color: '#3B82F6' },
];

function PendingItem({ item, type, navigate }) {
  const [hov, setHov] = useState(false);
  const cfg = TABS.find(t => t.key === type) || TABS[0];
  const name = item.user_prenom && item.user_nom
    ? `${item.user_prenom} ${item.user_nom}`
    : item.user_nom || item.nom_complet || '—';

  let detail = '';
  if (type === 'ts') detail = `${item.mois ? String(item.mois).padStart(2,'0') : '?'}/${item.annee || '?'}`;
  else if (type === 'abs') detail = `${fmtDateShort(item.date_debut)} → ${fmtDateShort(item.date_fin)}`;
  else if (type === 'nf')  detail = item.montant_ttc ? `${parseFloat(item.montant_ttc).toFixed(0)} €` : item.designation || '';
  else if (type === 'arrets') detail = `${fmtDateShort(item.date_debut)} → ${fmtDateShort(item.date_fin)} · ${item.nb_jours}j`;
  else if (type === 'docs') detail = item.document_type ? item.document_type.replace(/_/g, ' ') : item.title || '';

  const to = type === 'ts'  ? `/timesheets/${item.annee}/${item.mois}`
           : type === 'nf'  ? `/notes-frais/${item.id}`
           : type === 'abs' ? `/absences/${item.id}`
           : null;

  return (
    <div
      onClick={() => navigate('/rh-validations')}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
        borderRadius: 9, cursor: 'pointer',
        background: hov ? 'var(--bg-elevated)' : 'transparent',
        transition: 'background 0.13s',
      }}
    >
      <div style={{ width: 34, height: 34, borderRadius: 9, background: `${cfg.color}12`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <cfg.icon size={16} color={cfg.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.8375rem', fontWeight: 700, color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{detail}</div>
      </div>
      <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
    </div>
  );
}

function PendingSection({ pendingTS, pendingNF, pendingAbs, pendingArrets, pendingDocs, navigate }) {
  const [activeTab, setActiveTab] = useState('ts');
  const counts = {
    ts:     pendingTS.length,
    abs:    pendingAbs.length,
    nf:     pendingNF.length,
    arrets: pendingArrets.length,
    docs:   pendingDocs.length,
  };
  const data = { ts: pendingTS, abs: pendingAbs, nf: pendingNF, arrets: pendingArrets, docs: pendingDocs };
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 18px 12px' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
            À valider
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {total} élément{total !== 1 ? 's' : ''} en attente
          </div>
        </div>
        <button
          onClick={() => navigate('/rh-validations')}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px',
                   borderRadius: 8, border: '1px solid var(--border)',
                   background: 'var(--bg-elevated)', cursor: 'pointer',
                   color: 'var(--text-secondary)', fontSize: '0.78rem', fontWeight: 600 }}>
          Tout voir <ArrowRight size={12}/>
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, padding: '0 14px 10px',
                    borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 11px', borderRadius: 7, border: 'none', cursor: 'pointer',
              background: activeTab === t.key ? `${t.color}14` : 'transparent',
              color:      activeTab === t.key ? t.color : 'var(--text-muted)',
              fontWeight: activeTab === t.key ? 700 : 500,
              fontSize: '0.78rem', transition: 'all 0.12s',
            }}
          >
            <t.icon size={13}/>
            {t.label}
            {counts[t.key] > 0 && (
              <span style={{ whiteSpace: 'nowrap', padding: '1px 6px', borderRadius: 10, fontSize: '0.67rem',
                             fontWeight: 700, background: `${t.color}20`, color: t.color }}>
                {counts[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      <div style={{ maxHeight: 280, overflowY: 'auto', padding: '6px 0' }}>
        {data[activeTab].length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
            <CheckCircle2 size={28} style={{ opacity: 0.2, display: 'block', margin: '0 auto 8px' }}/>
            Aucun élément en attente
          </div>
        ) : (
          data[activeTab].slice(0, 12).map(item => (
            <PendingItem key={item.id} item={item} type={activeTab} navigate={navigate} />
          ))
        )}
      </div>
    </div>
  );
}

// ── Upcoming absences ─────────────────────────────────────────
function AgendaSection({ upcoming }) {
  const today = new Date().toISOString().split('T')[0];

  if (upcoming.length === 0) {
    return (
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: 14, padding: '20px 18px' }}>
        <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)', marginBottom: 14 }}>
          Prochains congés
        </div>
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          <Calendar size={28} style={{ opacity: 0.2, display: 'block', margin: '0 auto 8px' }}/>
          Aucun congé à venir
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
          Prochains congés validés
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {upcoming.length} absence{upcoming.length > 1 ? 's' : ''} prévue{upcoming.length > 1 ? 's' : ''}
        </div>
      </div>
      <div style={{ padding: '8px 0', maxHeight: 260, overflowY: 'auto' }}>
        {upcoming.map((a, i) => {
          const inDays = daysUntil(a.date_debut);
          const isNow  = a.date_debut <= today && a.date_fin >= today;
          const jours  = daysBetween(a.date_debut, a.date_fin);
          const name   = a.user_prenom && a.user_nom ? `${a.user_prenom} ${a.user_nom}` : '—';
          const initls = `${(a.user_prenom || '')[0] || ''}${(a.user_nom || '')[0] || ''}`.toUpperCase();
          const color  = isNow ? '#059669' : inDays <= 3 ? '#D97706' : '#0891B2';

          return (
            <div key={a.id || i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 16px',
              borderBottom: i < upcoming.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                            background: `${color}14`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 800, fontSize: '0.72rem', color }}>
                {initls || '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {name}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  {fmtDateShort(a.date_debut)} → {fmtDateShort(a.date_fin)} · {jours}j
                </div>
              </div>
              <div style={{ flexShrink: 0 }}>
                {isNow ? (
                  <span style={{ whiteSpace: 'nowrap', padding: '2px 8px', borderRadius: 20, fontSize: '0.65rem',
                                  fontWeight: 700, background: `${color}14`, color }}>
                    En cours
                  </span>
                ) : (
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    J+{inDays}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Quick actions ─────────────────────────────────────────────
const RH_ACTIONS = [
  { icon: Timer,          color: '#0891B2', label: 'Ma feuille de temps', sub: 'Saisir mon activité',   to: '/timesheets/history' },
  { icon: ReceiptText,    color: '#D97706', label: 'Mes notes de frais',  sub: 'Déposer un justificatif', to: '/notes-frais/nouveau' },
  { icon: CalendarX,      color: '#7C3AED', label: 'Demander un congé',   sub: 'Poser une absence',     to: '/absences/new' },
  { icon: Activity,       color: '#DC2626', label: 'Arrêt maladie',       sub: 'Déclarer un arrêt',     to: '/arrets-maladie/nouveau' },
  { icon: ClipboardCheck, color: '#059669', label: 'Validations',         sub: 'Traiter les demandes',  to: '/rh-validations' },
  { icon: FolderOpen,     color: '#3B82F6', label: 'Documents RH',        sub: 'Dossiers salariés',     to: '/rh-documents' },
];

function ActionItem({ a, navigate }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={() => navigate(a.to)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
        cursor: 'pointer', background: hov ? 'var(--bg-elevated)' : 'var(--bg-card)',
        transition: 'background 0.12s',
      }}
    >
      <div style={{ width: 32, height: 32, borderRadius: 8, background: `${a.color}12`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <a.icon size={15} color={a.color}/>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.775rem', fontWeight: 700, color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {a.label}
        </div>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{a.sub}</div>
      </div>
    </div>
  );
}

function QuickActions({ navigate }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontWeight: 800, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
          Actions rapides
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: 'var(--border)' }}>
        {RH_ACTIONS.map((a, i) => <ActionItem key={i} a={a} navigate={navigate} />)}
      </div>
    </div>
  );
}

// ── Team overview strip ────────────────────────────────────────
function TeamOverview({ users }) {
  if (!users.length) return null;
  const actifs = users.filter(u => u.is_active !== false);

  const byRole = {};
  actifs.forEach(u => {
    const r = (u.role || 'autre').toLowerCase();
    byRole[r] = (byRole[r] || 0) + 1;
  });

  const ROLE_COLORS = {
    collaborateur: '#0891B2',
    admin: '#DC2626',
    business_manager: '#3B82F6',
    ingenieur_affaires: '#3B82F6',
    talent_acquisition: '#EA580C',
    referent_technique: '#0891B2',
    rh: '#059669',
  };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 14, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
          Équipe
        </div>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
          {actifs.length} actif{actifs.length > 1 ? 's' : ''}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {Object.entries(byRole)
          .sort((a, b) => b[1] - a[1])
          .map(([role, count]) => {
            const color = ROLE_COLORS[role] || '#8B8FA8';
            const label = role.replace(/_/g, ' ');
            return (
              <div key={role} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 20,
                background: `${color}10`, border: `1px solid ${color}25`,
              }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }}/>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'capitalize' }}>
                  {label}
                </span>
                <span style={{ fontSize: '0.72rem', color, fontWeight: 800 }}>·{count}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function RhDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading,       setLoading]       = useState(true);
  const [pendingTS,     setPendingTS]     = useState([]);
  const [pendingNF,     setPendingNF]     = useState([]);
  const [pendingAbs,    setPendingAbs]    = useState([]);
  const [pendingArrets, setPendingArrets] = useState([]);
  const [pendingDocs,   setPendingDocs]   = useState([]);
  const [upcomingAbs,   setUpcomingAbs]   = useState([]);
  const [users,         setUsers]         = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const [ts, nf, abs, arr, docs, upcoming] = await Promise.allSettled([
        validationsAPI.listTimesheets({ statut: 'SOUMIS' }),
        validationsAPI.listNotesFrais({ statut: 'SOUMIS' }),
        validationsAPI.listAbsences({ statut: 'SOUMIS' }),
        validationsAPI.listArrets({ statut: 'SOUMIS' }),
        validationsAPI.listDocuments({ statut: 'SOUMIS' }),
        validationsAPI.listAbsences({ statut: 'VALIDE' }),
      ]);

      if (ts.status === 'fulfilled')   setPendingTS(ts.value.data || []);
      if (nf.status === 'fulfilled')   setPendingNF(nf.value.data || []);
      if (abs.status === 'fulfilled')  setPendingAbs(abs.value.data || []);
      if (arr.status === 'fulfilled')  setPendingArrets(arr.value.data || []);
      if (docs.status === 'fulfilled') setPendingDocs(docs.value.data || []);
      if (upcoming.status === 'fulfilled') {
        const up = (upcoming.value.data || [])
          .filter(a => a.date_fin >= today)
          .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut))
          .slice(0, 10);
        setUpcomingAbs(up);
      }

      // Salariés (via endpoint RH)
      try {
        const sal = await api.get('/absences-imposees/salaries');
        setUsers(sal.data || []);
      } catch { /* optionnel */ }

    } catch (e) {
      toast.error('Erreur chargement dashboard RH');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalPending = pendingTS.length + pendingNF.length + pendingAbs.length + pendingArrets.length + pendingDocs.length;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <div style={{
          background: 'linear-gradient(135deg, #064e3b 0%, #065f46 55%, #047857 100%)',
          padding: '32px 36px 28px', marginBottom: 28,
        }}>
          <div style={{ height: 60, background: 'rgba(255,255,255,0.06)', borderRadius: 10, width: 280 }}/>
        </div>
        <div style={{ padding: '0 32px', display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div className="spinner" style={{ width: 32, height: 32 }}/>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-main)' }}>
      <Hero user={user} totalPending={totalPending} onRefresh={load} loading={loading} />

      {/* KPI Row */}
      <div style={{ padding: '24px 32px 0',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 14 }}>
        <KpiCard
          icon={Users} color="#059669"
          value={users.length || '—'}
          label="Collaborateurs"
          sub="actifs"
          navigate={navigate}
        />
        <KpiCard
          icon={Timer} color="#0891B2"
          value={pendingTS.length}
          label="Feuilles"
          sub="à valider"
          to="/rh-validations"
          urgent
          navigate={navigate}
        />
        <KpiCard
          icon={CalendarX} color="#7C3AED"
          value={pendingAbs.length}
          label="Congés"
          sub="à valider"
          to="/rh-validations"
          urgent
          navigate={navigate}
        />
        <KpiCard
          icon={ReceiptText} color="#D97706"
          value={pendingNF.length}
          label="Frais"
          sub="à valider"
          to="/rh-validations"
          urgent
          navigate={navigate}
        />
        <KpiCard
          icon={Activity} color="#DC2626"
          value={pendingArrets.length}
          label="Arrêts"
          sub="à valider"
          to="/rh-validations"
          urgent
          navigate={navigate}
        />
        <KpiCard
          icon={FolderOpen} color="#3B82F6"
          value={pendingDocs.length}
          label="Documents"
          sub="à valider"
          to="/rh-validations"
          urgent
          navigate={navigate}
        />
      </div>

      {/* Main grid */}
      <div style={{ padding: '20px 32px 40px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 320px',
                    gap: 16,
                  }}>
        {/* Left: À valider */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <PendingSection
            pendingTS={pendingTS}
            pendingNF={pendingNF}
            pendingAbs={pendingAbs}
            pendingArrets={pendingArrets}
            pendingDocs={pendingDocs}
            navigate={navigate}
          />
          {users.length > 0 && <TeamOverview users={users} />}
        </div>

        {/* Right: Agenda + Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <QuickActions navigate={navigate} />
          <AgendaSection upcoming={upcomingAbs} />
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
